import { useEffect, useState, useRef, useMemo } from "react";
import CropPng from "./CropPng";
import DesktopLayerSelector from "./DesktopLayerSelector";
import DesktopToolbar from "./DesktopToolBar";
import TraceToolsPopover from "./TraceToolsPopover";
import CropIcon from "../../../assets/SvgEditor/Crop.svg";
import SplitCanvas from "./SplitCanvas";
import SVGTraceTopBar from "../../SvgEditor/components/SVGTraceTopBar";
import { useAppliedColorPreview } from "./useAppliedColorPreview";
import { getColors } from "../helpers/getColors";
import ColorAnalyzer from "./ColorAnalyzer";
import AIToolkitBar from "./AIToolkitBar";
import ThreadColorHeading from "./ThreadColorHeading";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { IonIcon, IonSpinner } from "@ionic/react";
import { helpCircleOutline, imageOutline, lockClosedOutline } from "ionicons/icons";
import Submit from "./Submit";
import { ChevronRight } from "react-feather";
import BitmapLayerDebugModal from "./BitmapLayerDebugModal";
import { resetBulkConversionState } from "../../../redux/slices/BulkConversion";
import { useAppDispatch } from "../../../redux/store";
import { useHistory } from "react-router-dom";
import { useTutorial } from "../../Tutorial/TutorialProvider";
import { useAuth } from "../../../contexts/AuthContext";
import { isNativeiOS } from "../../../lib/utils/platform";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import PaywallModal from "../../PaywallModal/PaywallModal";
import { downloadBlob } from "../../../utils/nativeDownload";
import ConversionLoadingOverlay from "./ConversionLoadingOverlay";
import { isEmbroideryTrace } from "../../../config/appVariant";

const SubmitButton = ({ previewLoading, ...props }) => (
  <Submit {...props} data-tutorial="download-button">
    {previewLoading ? (
      <span>Loading...</span>
    ) : (
      <>
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {isEmbroideryTrace ? "Next: Stitch Editor" : "Next"}
          <ChevronRight size={20} style={{ marginRight: "4px" }} />
        </span>
      </>
    )}
  </Submit>
);

const ConverterDesktop = ({
  onRetracedArtwork,
  scale,
  setScale,
  file,
  setFile,
  clearFields,
  crop,
  setIsCropVisible,
  screenSize,
  setClickedColor,
  imageDimensions,
  setImageDimensions,
  originalDimensions,
  setCropGlobal,
  largeImageWarning,
  isCropVisible,
  setIsModalVisible,
  downloadSVGLink,
  isLoading,
  smoothness,
  setSmoothness,
  despeckle,
  setDespeckle,
  simplifyCurves,
  setSimplifyCurves,
  ai,
  setAi,
  colors,
  setColors,
  setSvgPreview,
  setPreviewReady,
  setSvg,
  setIsLoading,
  setDownloadSVGLink,
  setShareLink,
  svgPreview,
  svgRaw,
  saveToCloud,
  setLoadingConverter,
  setConverterOpen,
  setCrop,
  setOriginalDimensions,
  setLargeImageWarning,
  wasUpscaled,
  setWasUpscaled,
  upscaleReason,
  guestMode = false,
  pendingFileId = null,
  onPaywallTrigger,
  aiAnalysisResult,
  useMorphology = false,
  setUseMorphology,
}) => {
  const [showColorInfo, setShowColorInfo] = useState(() => {
    const savedState = localStorage.getItem("colorSelectInfoShown");
    return savedState === null;
  });
  const [showBitmapDebug, setShowBitmapDebug] = useState(false);
  const [useSuperpixels, setUseSuperpixels] = useState(true);
  const [colorDetectionMethod, setColorDetectionMethod] = useState("analyzer"); // "analyzer" or "legacy"
  const [showAddColorPrompt, setShowAddColorPrompt] = useState(false);
  const [showRetraceTip, setShowRetraceTip] = useState(() => {
    const savedState = localStorage.getItem("retraceTipShown_v2");
    return savedState === null;
  });
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);
  
  const { user, userIsPremium } = useAuth();

  // Ref to track when guest mode has manually set the preview (skip next debounce)
  const guestManualPreviewRef = useRef(false);
  
  // State for guest mode: stores the retraced/enhanced file for SVG processing
  // While `file` is used for canvas display, this is used for SVG generation
  const [guestProcessingFile, setGuestProcessingFile] = useState(null);

  const handleColorInfoDismiss = () => {
    setShowColorInfo(false);
    localStorage.setItem("colorSelectInfoShown", "true");
  };

  const handleRetraceTipDismiss = () => {
    setShowRetraceTip(false);
    localStorage.setItem("retraceTipShown_v2", "true");
  };

  const handleDownloadPng = async () => {
    if (!file) return;

    if (guestMode) {
      if (onPaywallTrigger) onPaywallTrigger("download-png");
      return;
    }

    if (!userIsPremium) {
      if (isNativeiOS()) {
        setShowIOSPaywall(true);
      } else if (onPaywallTrigger) {
        onPaywallTrigger("download-png");
      } else {
        setShowPaywallModal(true);
      }
      return;
    }

    setIsDownloadingPng(true);
    try {
      const res = await fetch(file);
      const blob = await res.blob();
      await downloadBlob(blob, "original-image.png");
    } catch (err) {
      console.error("PNG download failed:", err);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  // Function to re-detect colors when algorithm changes
  const redetectColorsForAlgorithm = async (useSuperpixelsAlgorithm) => {
    if (!file) return;
    
    console.log("Re-detecting colors for algorithm:", useSuperpixelsAlgorithm ? "superpixels" : "legacy");
    
    try {
      let newColors;
      
      if (useSuperpixelsAlgorithm) {
        // Use ColorAnalyzer for superpixel algorithm
        console.log("Using ColorAnalyzer for color detection");
        const hexColors = await ColorAnalyzer.processImage(file);
        newColors = hexColors.map((color) => [color.slice(1), true]);
      } else {
        // Use legacy getColors for detailed algorithm
        console.log("Using legacy getColors for color detection");
        const hexColors = await getColors(file);
        newColors = hexColors.map((color) => [color.slice(1), true]);
      }
      
      console.log("New colors detected:", newColors);
      setColors(newColors);
      applyColors(newColors);
    } catch (error) {
      console.error("Error re-detecting colors:", error);
    }
  };

  useEffect(() => {
    setLoadingConverter(false);
    
    // Trigger tutorial action when converter is fully loaded
    setTimeout(() => {
      if (window.tutorialTriggerAction && window.tutorialGetCurrentStep) {
        const currentStep = window.tutorialGetCurrentStep();
        const stepId = window.tutorialGetCurrentStepId?.();
        console.log('🎯 ConverterDesktop: Current tutorial step:', currentStep, stepId);
        // Only skip the SVG converter tutorial's upload step. Index 2 is also
        // embroidery's processing wait, which must still receive this signal.
        if (stepId !== 'upload-or-generate') {
          console.log('🎯 ConverterDesktop: Triggering converter-opened for tutorial');
          window.tutorialTriggerAction('converter-opened', { source: 'desktop-converter' });
        } else {
          console.log('🎯 ConverterDesktop: Skipping auto converter-opened - on upload step');
        }
      }
    }, 500); // Small delay to ensure everything is rendered
  }, []);

  // Expose color re-detection function globally for TraceToolsPopover
  useEffect(() => {
    window.redetectColorsForAlgorithm = redetectColorsForAlgorithm;
    
    return () => {
      delete window.redetectColorsForAlgorithm;
    };
  }, [redetectColorsForAlgorithm]);

  // Handle "Edit" button click on desktop
  const handleAddNewColorClick = () => {
    setShowAddColorPrompt(true);
  };

  // Compute the file to process - this determines which file is used for SVG generation
  // Using useMemo to avoid unnecessary re-processing when guestMode changes but file doesn't
  const fileToProcess = useMemo(() => {
    return (guestMode && guestProcessingFile) ? guestProcessingFile : file;
  }, [guestMode, guestProcessingFile, file]);

  const { hasUnappliedColorChanges, canApplyColors, applyColors,
    previewLoading, previewError, isPreviewCurrent } = useAppliedColorPreview({
    file: fileToProcess, colors, crop, despeckle, simplifyCurves, smoothness,
    ai, originalDimensions, useSuperpixels, useMorphology,
    setSvg, setSvgPreview, setDownloadSVGLink,
  });
  const handleApplyColors = () => applyColors();

  const dispatch = useAppDispatch();
  const history = useHistory();
  const { startTutorial, hasCompletedTutorial } = useTutorial();

  const submitButtonProps = {
    setFile,
    setSvgPreview,
    setPreviewReady,
    setSvg,
    originalDimensions,
    crop,
    despeckle,
    simplifyCurves,
    smoothness,
    colors,
    file,
    setIsLoading,
    isLoading,
    setDownloadSVGLink,
    setShareLink,
    setIsModalVisible,
    ai,
    saveToCloud,
    previewLoading,
    loading: previewLoading,
    disabled: previewLoading || hasUnappliedColorChanges || !isPreviewCurrent,
  };

  const handleClose = () => {
    clearFields();
    setConverterOpen(false);
    dispatch(resetBulkConversionState());
    history.push("/");
  };

  return (
    <>
      <BitmapLayerDebugModal
        isOpen={showBitmapDebug}
        onDismiss={() => setShowBitmapDebug(false)}
        file={file}
        colors={colors}
        crop={crop}
      />
      
      <SVGTraceTopBar
        label="CONVERTER"
        leftContent={
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {!guestMode && (
              <button
                onClick={handleClose}
                title="Close"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "6px",
                  border: "none",
                  background: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }} />
              </button>
            )}
            <button
              onClick={() => setIsCropVisible(true)}
              title="Crop"
              style={{
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "10px",
                color: "rgba(255,255,255,0.7)",
                background: "rgba(255,255,255,0.1)",
                border: "none",
                padding: "0 8px",
                borderRadius: "6px",
                transition: "all 0.15s",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                height: "26px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            >
              <img src={CropIcon} alt="Crop" style={{ width: "13px", height: "13px", opacity: 0.7, filter: "invert(1)" }} />
              Crop
            </button>
            <button
              onClick={handleDownloadPng}
              disabled={isDownloadingPng || !file}
              title={userIsPremium ? "Download PNG" : "Download PNG (Pro)"}
              style={{
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "10px",
                color: userIsPremium ? "rgba(255,255,255,0.7)" : "#F453C4",
                background: userIsPremium ? "rgba(255,255,255,0.1)" : "rgba(244,83,196,0.15)",
                border: "none",
                padding: "0 8px",
                borderRadius: "6px",
                transition: "all 0.15s",
                cursor: (isDownloadingPng || !file) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                height: "26px",
                opacity: (isDownloadingPng || !file) ? 0.5 : 1,
              }}
            >
              {isDownloadingPng ? (
                <IonSpinner name="crescent" style={{ width: "12px", height: "12px" }} />
              ) : (
                <>
                  <IonIcon icon={imageOutline} style={{ fontSize: "13px" }} />
                  PNG
                  {!userIsPremium && (
                    <IonIcon icon={lockClosedOutline} style={{ fontSize: "9px", marginLeft: "-2px" }} />
                  )}
                </>
              )}
            </button>
          </div>
        }
        rightContent={
          <div data-tutorial="conversion-settings" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <TraceToolsPopover
              smoothness={smoothness}
              setSmoothness={setSmoothness}
              despeckle={despeckle}
              setDespeckle={setDespeckle}
              simplifyCurves={simplifyCurves}
              setSimplifyCurves={setSimplifyCurves}
              imageDimensions={imageDimensions}
              useSuperpixels={useSuperpixels}
              setUseSuperpixels={setUseSuperpixels}
              useMorphology={useMorphology}
              setUseMorphology={setUseMorphology}
              topBarStyle
            />
            <button
              style={{
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "10px",
                color: "rgba(255,255,255,0.5)",
                background: "rgba(255,255,255,0.1)",
                border: "none",
                padding: "0 8px",
                borderRadius: "6px",
                transition: "all 0.15s",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                height: "26px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
              onClick={() => startTutorial('converter', 6)}
              title={hasCompletedTutorial('converter') ? 'Replay Tutorial' : 'Start Tutorial'}
            >
              <IonIcon icon={helpCircleOutline} style={{ fontSize: "13px" }} />
              Tutorial
            </button>
          </div>
        }
      />
      
      {/* Main content area — full viewport minus 36px header */}
      <div style={{
        position: "relative",
        width: "100%",
        height: "calc(100vh - 36px)",
        overflow: "hidden",
        backgroundColor: "#f1f3f5",
      }}>
        {/* Floating bottom-center: Next Open SVG button */}
        <div style={{
          position: "absolute",
          bottom: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 20,
          pointerEvents: "auto",
        }}>
          <SubmitButton {...submitButtonProps} />
        </div>
        {/* ── Main split layout: 50/50 synced view + floating color panel ── */}
        <div
          data-tutorial="preview-area"
          style={{ display: "flex", height: "100%", width: "100%", position: "relative" }}
        >
          {/* Full-width split canvas area */}
          <div
            data-tutorial="original-image"
            style={{
              flex: "1",
              height: "100%",
              position: "relative",
              backgroundColor: "#f1f3f5",
              minWidth: 0,
            }}
          >

            {/* Two-tier toolbar — centered, below floating controls */}
            <div style={{
              position: "absolute",
              top: "48px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 11,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}>
              {/* Tier 1: AI Toolkit */}
              <AIToolkitBar
                onRetracedArtwork={onRetracedArtwork}
                file={file}
                setFile={setFile}
                colors={colors}
                setColors={setColors}
                crop={crop}
                setCrop={setCrop}
                setImageDimensions={setImageDimensions}
                despeckle={despeckle}
                setDespeckle={setDespeckle}
                setOriginalDimensions={setOriginalDimensions}
                setLargeImageWarning={setLargeImageWarning}
                autoTriggerRetrace={wasUpscaled}
                setWasUpscaled={setWasUpscaled}
                guestMode={guestMode}
                pendingFileId={pendingFileId}
                setSvgPreview={setSvgPreview}
                setSvg={setSvg}
                simplifyCurves={simplifyCurves}
                smoothness={smoothness}
                ai={ai}
                originalDimensions={originalDimensions}
                useSuperpixels={useSuperpixels}
                onPaywallTrigger={onPaywallTrigger}
                setGuestProcessingFile={setGuestProcessingFile}
              />

              {/* Tier 2: Detected Colors bar */}
              {colors && colors.length > 0 && (
                <div
                  data-tutorial="color-layers"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderRadius: "10px",
                    padding: "5px 10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    maxWidth: "min(720px, 92vw)",
                  }}
                >
                  {isEmbroideryTrace && <div style={{ flexBasis: "100%" }}><ThreadColorHeading colors={colors} /></div>}
                  {previewError && <span role="alert" style={{ flexBasis: "100%", fontSize: "12px", color: "#b91c1c" }}>{previewError}</span>}
                  <DesktopLayerSelector
                    colors={colors}
                    setColors={setColors}
                    imageDimensions={imageDimensions}
                    file={file}
                    setFile={setFile}
                    isLoading={isLoading}
                    submitBtn={null}
                    onOpenBitmapDebug={() => setShowBitmapDebug(true)}
                    crop={crop}
                    compact={true}
                    onAddNewClick={handleAddNewColorClick}
                  />
                  {(hasUnappliedColorChanges || previewError) && (
                    <span
                      style={{
                        width: "1px",
                        height: "20px",
                        background: "rgba(0,0,0,0.10)",
                        flexShrink: 0,
                        margin: "0 2px",
                      }}
                    />
                  )}
                  {(hasUnappliedColorChanges || previewError) && (
                    <button
                      onClick={handleApplyColors}
                      disabled={previewLoading || !canApplyColors}
                      title="Re-trace with your color changes"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        flexShrink: 0,
                        height: "28px",
                        padding: "0 12px",
                        borderRadius: "6px",
                        border: "none",
                        background: (previewLoading || !canApplyColors)
                          ? "rgba(200,200,200,0.7)"
                          : "linear-gradient(135deg, #e83e8c 0%, #d63384 100%)",
                        color: (previewLoading || !canApplyColors) ? "#999" : "white",
                        fontFamily: "Clash Grotesk",
                        fontSize: "11px",
                        fontWeight: 600,
                        cursor: (previewLoading || !canApplyColors) ? "not-allowed" : "pointer",
                        boxShadow: (previewLoading || !canApplyColors)
                          ? "none"
                          : "0 0 8px rgba(232, 62, 140, 0.45)",
                        transition: "all 0.15s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: (previewLoading || !canApplyColors) ? "#999" : "white",
                          flexShrink: 0,
                        }}
                      />
                      {previewError ? "Retry preview" : "Apply Changes"}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Low-res tip - shown when auto retrace is triggered */}
            {showRetraceTip && wasUpscaled && (
              <div
                style={{
                  position: "absolute",
                  top: "130px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "10px",
                  padding: "8px 14px",
                  boxShadow: "0 4px 16px rgba(102, 126, 234, 0.25)",
                  maxWidth: "280px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  fontFamily: "Clash Grotesk",
                  color: "white",
                  fontSize: "11px",
                  animation: "slideUpFade 0.4s ease-out",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "18px", flexShrink: 0 }}>✨</span>
                  <div style={{ flex: 1, lineHeight: 1.4 }}>
                    Your image is <strong style={{ fontWeight: 700 }}>low resolution</strong> and would benefit from <strong style={{ fontWeight: 700 }}>Retrace</strong>
                  </div>
                  <button
                    onClick={handleRetraceTipDismiss}
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      borderRadius: "6px",
                      width: "22px",
                      height: "22px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "600",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            
            {/* "Click to Add Color" Dialog/Prompt */}
            {showAddColorPrompt && (
              <>
                {/* Backdrop overlay */}
                <div
                  onClick={() => setShowAddColorPrompt(false)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 14,
                    animation: "fadeIn 0.2s ease-out",
                  }}
                />
                {/* Dialog pointing to main image */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 15,
                    background: "linear-gradient(135deg, #e83e8c 0%, #d63384 100%)",
                    borderRadius: "16px",
                    padding: "24px 32px",
                    boxShadow: "0 16px 48px rgba(232, 62, 140, 0.45), 0 0 0 3px rgba(232, 62, 140, 0.2)",
                    width: "340px",
                    maxWidth: "90%",
                    border: "2px solid rgba(255, 255, 255, 0.35)",
                    fontFamily: "Clash Grotesk",
                    color: "white",
                    textAlign: "center",
                    animation: "fadeInScale 0.3s ease-out",
                  }}
                >
                  <div style={{ fontSize: "56px", marginBottom: "16px", lineHeight: 1 }}>👈</div>
                  <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "10px", lineHeight: 1.3 }}>
                    Click the Main Image
                  </div>
                  <div style={{ fontSize: "14px", opacity: 0.95, lineHeight: 1.5, marginBottom: "4px" }}>
                    {isEmbroideryTrace ? "Click the artwork on the left to add a thread color." : "Click anywhere on the original image to the left to pick and add a color"}
                  </div>
                  <button
                    onClick={() => setShowAddColorPrompt(false)}
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      background: "rgba(255, 255, 255, 0.25)",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "white",
                      fontSize: "20px",
                      fontWeight: "bold",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.4)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)"}
                  >
                    ×
                  </button>
                </div>
              </>
            )}
              
            <SplitCanvas
              screenSize={screenSize}
              crop={crop}
              clearFields={clearFields}
              file={file}
              scale={scale}
              setScale={setScale}
              setClickedColor={setClickedColor}
              svgPreview={svgPreview}
              svgRaw={svgRaw}
              previewLoading={previewLoading}
              guestMode={guestMode}
              downloadSVGLink={downloadSVGLink}
              saveToCloud={saveToCloud}
              dstPreviewData={null}
              dstRawBase64={null}
              isDstGenerating={false}
              dstStatus=""
              colors={colors}
            />
          </div>

        </div>

        <CropPng
          setScale={setScale}
          setImageDimensions={setImageDimensions}
          originalDimensions={originalDimensions}
          cropGlobal={crop}
          setCropGlobal={setCropGlobal}
          largeImageWarning={largeImageWarning}
          file={file}
          isModalVisible={isCropVisible}
          setIsModalVisible={setIsCropVisible}
          downloadSVGLink={downloadSVGLink}
          isLoading={isLoading}
        />
      </div>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="export"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}

      {!onPaywallTrigger && !isNativeiOS() && (
        <PaywallModal
          isVisible={showPaywallModal}
          onClose={() => setShowPaywallModal(false)}
          isAuthenticated={!!user}
          isPremium={userIsPremium}
          action="download-png"
        />
      )}

      <ConversionLoadingOverlay visible={previewLoading} />
    </>
  );
};

export default ConverterDesktop;
